/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, X, AlertTriangle, Loader2, Settings } from "lucide-react";
import api, { formatErr } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { useSite } from "@/context/SiteContext";

const ELEMENT_ID = "vb-qr-reader";

/**
 * QR scanner modal — hybrid permission flow:
 *  - On mount we go straight to the camera (no primer screen). The OS
 *    permission prompt only ever fires on first use; subsequent scans are
 *    instant.
 *  - If the OS denies, we show a recovery screen with browser-specific
 *    instructions instead of a dead end.
 *
 *  Steps: "scanning" → "resolving" → (close + nav)  |  "denied" | "error"
 */
export default function QRScannerModal({ onClose }) {
  const [step, setStep] = useState("scanning");
  const [err, setErr] = useState("");
  const scannerRef = useRef(null);
  const startedRef = useRef(false);
  const navigate = useNavigate();
  const { setActiveSite } = useSite();

  const stop = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); } catch (e) { console.debug("Scanner already stopped:", e); }
      try { await scannerRef.current.clear(); } catch (e) { console.debug("Scanner already cleared:", e); }
      scannerRef.current = null;
    }
  };

  const start = async () => {
    if (startedRef.current) return;
    startedRef.current = true;
    setStep("scanning"); setErr("");
    try {
      const config = { fps: 10, qrbox: { width: 240, height: 240 }, rememberLastUsedCamera: true };
      const qr = new Html5Qrcode(ELEMENT_ID, /* verbose= */ false);
      scannerRef.current = qr;
      await qr.start({ facingMode: "environment" }, config, onDecode, () => { /* ignore frame errors */ });
    } catch (e) {
      const name = e?.name || "";
      const msg = (e?.message || "").toLowerCase();
      if (name === "NotAllowedError" || msg.includes("permission")) {
        setStep("denied");
      } else if (name === "NotFoundError" || msg.includes("no camera")) {
        setErr("No camera found on this device.");
        setStep("error");
      } else {
        setErr(e?.message || "Could not start camera.");
        setStep("error");
      }
      startedRef.current = false;
    }
  };

  // Auto-start on mount.
  useEffect(() => {
    // small defer so the modal element exists before getUserMedia
    const t = setTimeout(start, 50);
    return () => { clearTimeout(t); stop(); };
  }, []);

  const onDecode = async (decodedText) => {
    if (step === "resolving") return;
    setStep("resolving");
    await stop();
    try {
      const { data } = await api.post("/scan/resolve", { token: decodedText });
      onClose();
      // Seamless multi-site UX: switch the active site to the scanned item's
      // site so the destination page is correctly scoped — no need for the
      // user to manually set the right site before scanning.
      const targetSite =
        data.site?.id || data.equipment?.site_id || data.facility?.site_id || null;
      if (targetSite) setActiveSite(targetSite);
      if (data.kind === "equipment") {
        navigate(`/app/scan/equipment/${data.equipment.id}`, { state: data });
      } else if (data.kind === "facility_hygiene") {
        // Per-facility hygiene QR — land on Daily Tracking (hygiene tab) with
        // the Log modal pre-opened for this facility. We route through
        // /app/daily-tracking directly so the query string survives — the
        // /app/daily-hygiene alias is a <Navigate> redirect that drops params.
        navigate(`/app/daily-tracking?tab=hygiene&facility=${encodeURIComponent(data.facility.id)}&action=log&from=qr`);
      } else if (data.kind === "facility_cleanliness") {
        navigate(`/app/daily-tracking?tab=cleanliness&facility=${encodeURIComponent(data.facility.id)}&action=log&from=qr`);
      } else if (data.kind === "cleanliness") {
        navigate(`/app/daily-tracking?tab=cleanliness&site_id=${encodeURIComponent(data.site.id)}&template=${encodeURIComponent(data.template_code)}&from=qr`);
      } else if (data.kind === "hygiene") {
        navigate(`/app/daily-tracking?tab=hygiene&site_id=${encodeURIComponent(data.site.id)}&from=qr`);
      } else if (data.kind === "food_prep") {
        navigate(`/app/food-prep?tab=scan&from=qr`);
      } else if (data.kind === "form") {
        navigate(`/app/forms?open=${encodeURIComponent(data.template.id)}&from=qr`);
      } else {
        setErr("This QR isn't supported yet."); setStep("error");
      }
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Couldn't resolve this QR.");
      setStep("error");
    }
  };

  const close = async () => { await stop(); onClose(); };

  const retry = async () => { await stop(); startedRef.current = false; start(); };

  return createPortal(
    <div className="vb-modal-bg" data-testid="qr-scanner-modal" onClick={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div className="vb-modal" style={{ maxWidth: 460 }}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="m-0 flex items-center gap-2"><Camera size={18} /> Scan QR label</h2>
          <button type="button" onClick={close} className="vb-btn ghost" data-testid="scanner-close"><X size={16} /></button>
        </div>

        {step === "scanning" && (
          <div data-testid="scanner-active">
            <div id={ELEMENT_ID} style={{ minHeight: 280, borderRadius: 12, overflow: "hidden", background: "#000" }} />
            <p className="text-xs opacity-60 mt-3 text-center">Hold the label steady inside the frame.</p>
          </div>
        )}

        {step === "resolving" && (
          <div className="text-center py-8" data-testid="scanner-resolving">
            <Loader2 className="animate-spin mx-auto mb-2" size={28} />
            <p className="text-sm opacity-80">Resolving label…</p>
          </div>
        )}

        {step === "denied" && (
          <div data-testid="scanner-denied">
            <div className="vb-pill err mb-3"><AlertTriangle size={14} /> Camera access was blocked</div>
            <p className="text-sm opacity-80 mb-3">
              The browser is blocking the camera. To re-enable it:
            </p>
            <ul className="text-xs opacity-80 mb-4 list-disc pl-5 space-y-1.5">
              <li><strong>iPhone (Safari)</strong>: Settings → Safari → Camera → Allow.</li>
              <li><strong>iPhone (Chrome)</strong>: tap <kbd>aA</kbd> in the address bar → Website Settings → Camera → Allow.</li>
              <li><strong>Android (Chrome)</strong>: tap the lock icon in the address bar → Permissions → Camera → Allow, then refresh.</li>
              <li><strong>Desktop</strong>: click the lock icon in the address bar → Site settings → Camera → Allow, then refresh.</li>
            </ul>
            <p className="text-xs opacity-60 mb-4 flex items-center gap-1"><Settings size={12} /> After enabling, tap "Try again" below.</p>
            <div className="flex justify-end gap-2">
              <button type="button" className="vb-btn ghost" onClick={close}>Close</button>
              <button type="button" className="vb-btn" onClick={retry} data-testid="scanner-retry">Try again</button>
            </div>
          </div>
        )}

        {step === "error" && (
          <div data-testid="scanner-error">
            <div className="vb-pill err mb-3"><AlertTriangle size={14} /> {err || "Something went wrong"}</div>
            <div className="flex justify-end gap-2">
              <button type="button" className="vb-btn ghost" onClick={close}>Close</button>
              <button type="button" className="vb-btn" onClick={retry} data-testid="scanner-retry">Try again</button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
